import type { PrintComExcludeRules } from '@/lib/printcom/printing-method-selection';

/** Evaluate supplier combinations using the configurator's resolved defaults. */
export function isExcludedByConfiguredOptionRules(
  excludes: PrintComExcludeRules | undefined,
  resolveProperty: (property: string) => { value: unknown; hidden?: boolean },
): boolean {
  return (excludes || []).some((rule) => {
    if (!Array.isArray(rule) || rule.length < 2) return false;
    return rule.every((condition) => {
      const property = condition?.property;
      const values = (condition?.options || []).map(String);
      if (!property || values.length === 0) return false;
      const { value, hidden } = resolveProperty(property);
      // Preserve the configurator's conservative handling of hidden defaults.
      if (hidden && (value === undefined || value === '')) return true;
      return value !== undefined && value !== '' && values.includes(String(value));
    });
  });
}

/**
 * Leveranciersregels koppelen een keuze vaak aan een minimumaantal, zoals
 * "zeefdruk vanaf 10 stuks" op textiel: een regel met `positions_* = 1pms`
 * en `copies = 1..9`. Het aantal is een getalveld, dus de conflictoplosser
 * die botsende opties laat meebewegen kan hier niets verschuiven; de klant zag
 * alleen "kan niet worden gemaakt" (19 okt, hoodie met zeefdruk op 5 stuks).
 *
 * Geeft het kleinste aantal boven het huidige waarbij geen enkele regel de
 * samenstelling nog uitsluit, of null als het aantal niet de oorzaak is.
 */
export function minimumCopiesForConfiguration(
  excludes: PrintComExcludeRules | undefined,
  resolveProperty: (property: string) => { value: unknown; hidden?: boolean },
  options: { copiesProperty?: string; maximum?: number } = {},
): number | null {
  const copiesProperty = options.copiesProperty || 'copies';
  const current = Number(resolveProperty(copiesProperty).value);
  if (!Number.isFinite(current) || current <= 0) return null;
  const withCopies = (copies: number) => (property: string) => (
    property === copiesProperty ? { value: copies } : resolveProperty(property)
  );
  if (!isExcludedByConfiguredOptionRules(excludes, withCopies(current))) return null;

  // Alleen regels met een aantalvoorwaarde kunnen door een ander aantal
  // verdwijnen. Kandidaten zijn de getallen direct boven elke uitgesloten
  // waarde; het kleinste geldige daarvan is het minimum.
  const candidates = new Set<number>();
  for (const rule of excludes || []) {
    if (!Array.isArray(rule)) continue;
    for (const condition of rule) {
      if (condition?.property !== copiesProperty) continue;
      for (const raw of condition.options || []) {
        const value = Number(raw);
        if (Number.isFinite(value) && value >= current) candidates.add(Math.floor(value) + 1);
      }
    }
  }
  const maximum = options.maximum ?? Number.POSITIVE_INFINITY;
  for (const candidate of Array.from(candidates).sort((a, b) => a - b)) {
    if (candidate > maximum) break;
    if (!isExcludedByConfiguredOptionRules(excludes, withCopies(candidate))) return candidate;
  }
  return null;
}

/**
 * Tegenhanger van `minimumCopiesForConfiguration` voor regels die een
 * MAXIMUM opleggen. Sleeves: een proefexemplaar (`sample_with_voucher` /
 * `sample_with_watermark`) is uitgesloten voor copies 2..2500, dus alleen 1
 * stuk kan. Omhoog zoeken vond daar niets.
 *
 * Geeft het grootste aantal onder het huidige waarbij geen enkele regel de
 * samenstelling nog uitsluit, of null als het aantal niet de oorzaak is.
 */
export function maximumCopiesForConfiguration(
  excludes: PrintComExcludeRules | undefined,
  resolveProperty: (property: string) => { value: unknown; hidden?: boolean },
  options: { copiesProperty?: string } = {},
): number | null {
  const copiesProperty = options.copiesProperty || 'copies';
  const current = Number(resolveProperty(copiesProperty).value);
  if (!Number.isFinite(current) || current <= 1) return null;
  const withCopies = (copies: number) => (property: string) => (
    property === copiesProperty ? { value: copies } : resolveProperty(property)
  );
  if (!isExcludedByConfiguredOptionRules(excludes, withCopies(current))) return null;

  const candidates = new Set<number>();
  for (const rule of excludes || []) {
    if (!Array.isArray(rule)) continue;
    for (const condition of rule) {
      if (condition?.property !== copiesProperty) continue;
      for (const raw of condition.options || []) {
        const value = Number(raw);
        if (Number.isFinite(value) && value <= current && value > 1) candidates.add(Math.ceil(value) - 1);
      }
    }
  }
  for (const candidate of Array.from(candidates).sort((a, b) => b - a)) {
    if (!isExcludedByConfiguredOptionRules(excludes, withCopies(candidate))) return candidate;
  }
  return null;
}

/**
 * Laten de regels voor deze samenstelling alleen 1 stuk toe? Waar: 1 is
 * toegestaan en elk ander bekend aantal (plus 2) is uitgesloten.
 */
export function allowsOnlySinglePiece(
  excludes: PrintComExcludeRules | undefined,
  resolveProperty: (property: string) => { value: unknown; hidden?: boolean },
  options: { copiesProperty?: string; candidateQuantities?: unknown[] } = {},
): boolean {
  if (!excludes?.length) return false;
  const copiesProperty = options.copiesProperty || 'copies';
  const withCopies = (copies: number) => (property: string) => (
    property === copiesProperty ? { value: copies } : resolveProperty(property)
  );
  if (isExcludedByConfiguredOptionRules(excludes, withCopies(1))) return false;
  const others = new Set<number>([2]);
  for (const raw of options.candidateQuantities || []) {
    const value = Number(raw);
    if (Number.isFinite(value) && value > 1) others.add(Math.floor(value));
  }
  return Array.from(others).every((copies) => isExcludedByConfiguredOptionRules(excludes, withCopies(copies)));
}
